import { t as tr } from '../i18n.js'
import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, ApiError, type SavedView, type ViewKind } from '../api.js'

/**
 * Saved-views bar: load a view (applies its config), save the current config
 * under a name, share (project admins), delete. `config` is whatever the host
 * view wants to persist — filters, sort, grouping.
 */
export function SavedViewsBar({ projectId, kind, current, onLoad, canShare }: {
  projectId: string; kind: ViewKind; current: Record<string, unknown>; onLoad: (config: Record<string, unknown>) => void; canShare: boolean
}) {
  const qc = useQueryClient()
  const views = useQuery({ queryKey: ['views', projectId, kind], queryFn: () => api<{ views: SavedView[] }>(`/projects/${projectId}/views?kind=${kind}`) })
  const [active, setActive] = useState<string>('')
  const [err, setErr] = useState<string | null>(null)
  const invalidate = () => qc.invalidateQueries({ queryKey: ['views', projectId, kind] })
  const save = useMutation({
    mutationFn: ({ name, shared }: { name: string; shared: boolean }) => api<{ view: SavedView }>(`/projects/${projectId}/views`, { method: 'POST', json: { name, kind, config: current, isShared: shared } }),
    onSuccess: (r) => { setActive(r.view.id); void invalidate() }, onError: (e) => setErr(e instanceof ApiError ? e.message : 'Could not save view'),
  })
  const update = useMutation({
    mutationFn: ({ id, body }: { id: string; body: Record<string, unknown> }) => api(`/projects/${projectId}/views/${id}`, { method: 'PATCH', json: body }),
    onSuccess: invalidate, onError: (e) => setErr(e instanceof ApiError ? e.message : 'Could not update view'),
  })
  const del = useMutation({ mutationFn: (id: string) => api(`/projects/${projectId}/views/${id}`, { method: 'DELETE' }), onSuccess: () => { setActive(''); void invalidate() } })
  const list = views.data?.views ?? []
  const activeView = list.find((v) => v.id === active)
  return (
    <div className="row viewsbar">
      <select value={active} onChange={(e) => { setActive(e.target.value); const v = list.find((x) => x.id === e.target.value); if (v) onLoad(v.config) }}>
        <option value="">{tr("Views…")}</option>
        {list.map((v) => <option key={v.id} value={v.id}>{v.isShared ? '★ ' : ''}{v.name}</option>)}
      </select>
      <button className="link" onClick={() => { const name = prompt('Save current filters as…'); if (name) { setErr(null); save.mutate({ name, shared: false }) } }}>Save view</button>
      {activeView?.mine && <button className="link" onClick={() => update.mutate({ id: activeView.id, body: { config: current } })}>Update</button>}
      {activeView && canShare && <button className="link" onClick={() => update.mutate({ id: activeView.id, body: { isShared: !activeView.isShared } })}>{activeView.isShared ? tr("Unshare") : tr("Share with project")}</button>}
      {activeView && (activeView.mine || canShare) && <button className="link danger" onClick={() => { if (confirm(`Delete view "${activeView.name}"?`)) del.mutate(activeView.id) }}>Delete</button>}
      {err && <span className="bad small">{err}</span>}
    </div>
  )
}
